import * as ko from 'knockout';
import {TBaseOrObservableOrFunctionType} from '../../index';
import {IDropdownButton, IDropdownLabel} from '../button';
import {IParams as IEllipsisDropdownParams} from '../button/ellipsis-dropdown';
import {getAnyValue} from '../../util/misc';
import {KOSubscriptionTracker} from '../../util/KOSubscriptionTracker';
import bulk_action_barHtml from './bulk-action-bar.html?raw';

export let template = bulk_action_barHtml;

/** Context passed into in-flight `onExecute` / `onSave` work. */
export interface IBulkActionCtx {
    /** Replaces the bar label while work runs. */
    reportProgress: (message: string) => void
    /** Aborted when the user cancels/closes an in-flight cancellable operation. */
    signal: AbortSignal
}

/** Nested intermediate-step config (all-or-nothing when present). */
export interface IBulkActionIntermediate<T> {
    /** Caller-owned label shown while the step is active (e.g. "2 permissions being edited"). */
    label: TBaseOrObservableOrFunctionType<string>
    cancelLabel?: string
    saveLabel?: string
    /** When false, Save is disabled; default true. */
    saveEnabled?: () => boolean
    onEnter?: (items: T[]) => void
    onCancel?: (items: T[]) => void
    onSave: (items: T[], ctx: IBulkActionCtx) => void | Promise<unknown>
}

/** One selectable operation; optional `intermediate` enters toolbar edit mode instead of immediate execute. */
export interface IBulkActionOperation<T> {
    label: IDropdownLabel
    icon?: TBaseOrObservableOrFunctionType<string>
    /** Overrides instance `clearSelection` after this operation finishes. */
    clearSelection?: boolean
    /** When false, the operation is omitted from the bar. */
    validator?: () => boolean
    /** When false, the operation stays visible but disabled. */
    enabled?: () => boolean
    /**
     * When true, Cancel/close stay available during in-flight work and abort `ctx.signal`.
     * Default false — no cancel/close while running.
     */
    cancellable?: boolean
    /** Immediate execute when `intermediate` is omitted. */
    onExecute?: (items: T[], ctx: IBulkActionCtx) => void | Promise<unknown>
    intermediate?: IBulkActionIntermediate<T>
}

export interface IBulkActionBarParams<T> {
    selected: KnockoutObservableArray<T>
    /** Caller-owned selection summary (e.g. "1 permission selected"). */
    selectedLabel: TBaseOrObservableOrFunctionType<string>
    operations: TBaseOrObservableOrFunctionType<IBulkActionOperation<T>[]>
    /** Instance default after execute/save; default true. Overridden per operation. */
    clearSelection?: boolean
    /** When set, used instead of clearing `selected` on dismiss (X / clear). */
    onClose?: () => void
}

type TMode = 'select' | 'intermediate'

export class viewModel<T> {
    private readonly selected: KnockoutObservableArray<T>;
    private readonly selectedLabel: TBaseOrObservableOrFunctionType<string>;
    private readonly operations: TBaseOrObservableOrFunctionType<IBulkActionOperation<T>[]>;
    private readonly instanceClearSelection: boolean;
    private readonly onClose: (() => void) | undefined;
    private readonly mode = ko.observable<TMode>('select');
    private readonly activeOperation = ko.observable<IBulkActionOperation<T> | null>(null);
    private readonly isRunning = ko.observable(false);
    private readonly progressLabel = ko.observable<string | null>(null);
    private readonly runningCancellable = ko.observable(false);
    private abortController: AbortController | null = null;
    /** Last non-empty selection — used when `selected` clears before `onCancel` can read it. */
    private lastSelected: T[] = [];
    private readonly kosub = new KOSubscriptionTracker();

    constructor(params: IBulkActionBarParams<T>) {
        this.selected = params.selected;
        this.selectedLabel = params.selectedLabel;
        this.operations = params.operations;
        this.instanceClearSelection = params.clearSelection ?? true;
        this.onClose = params.onClose;

        const initial = this.selected();
        if (initial.length) {
            this.lastSelected = initial.slice();
        }

        this.kosub.subscribe(this.selected, (items) => {
            if (items.length) {
                this.lastSelected = items.slice();
                return;
            }
            // External clear (deselect-all, parent) — same unwind as dismiss, not a silent UI reset.
            this.exitActiveWork(this.lastSelected);
        });
    }

    private isVisible = ko.pureComputed((): boolean => this.selected().length > 0);

    private isIntermediate = ko.pureComputed((): boolean => this.mode() === 'intermediate');

    private canCancelRunning = ko.pureComputed((): boolean => {
        return this.isRunning() && this.runningCancellable();
    });

    /** Cancel/close while idle, or while running only if the op is cancellable. */
    private canCancelOrClose = ko.pureComputed((): boolean => {
        return !this.isRunning() || this.runningCancellable();
    });

    private isSaveEnabled = ko.pureComputed((): boolean => {
        if (this.isRunning()) {
            return false;
        }
        return this.activeOperation()?.intermediate?.saveEnabled?.() ?? true;
    });

    private getSelectedLabel(): string {
        return this.progressLabel() ?? String(getAnyValue(this.selectedLabel) ?? '');
    }

    private getIntermediateLabel(): string {
        const progress = this.progressLabel();
        if (progress != null) {
            return progress;
        }
        const intermediate = this.activeOperation()?.intermediate;
        return intermediate ? String(getAnyValue(intermediate.label) ?? '') : '';
    }

    private getCancelLabel(): string {
        return this.activeOperation()?.intermediate?.cancelLabel ?? 'bulk.action.bar.cancel.all';
    }

    private getSaveLabel(): string {
        return this.activeOperation()?.intermediate?.saveLabel ?? 'bulk.action.bar.save.changes';
    }

    private getVisibleOperations(): IBulkActionOperation<T>[] {
        return (getAnyValue(this.operations) ?? []).filter((op) => op.validator?.() ?? true);
    }

    private getPrimaryOperation(): IBulkActionOperation<T> | null {
        return this.getVisibleOperations()[0] ?? null;
    }

    private getSecondaryOperations(): IBulkActionOperation<T>[] {
        return this.getVisibleOperations().slice(1);
    }

    private hasSingleSecondary = ko.pureComputed((): boolean => {
        return this.getSecondaryOperations().length === 1;
    });

    private hasSecondaryMenu = ko.pureComputed((): boolean => {
        return this.getSecondaryOperations().length > 1;
    });

    private getPrimaryButton(): IDropdownButton | null {
        const op = this.getPrimaryOperation();
        if (!op) {
            return null;
        }
        return this.toButton(op, 'btn bulk-action-bar__btn bulk-action-bar__btn--primary');
    }

    private getSingleSecondaryButton(): IDropdownButton | null {
        const op = this.getSecondaryOperations()[0];
        if (!op) {
            return null;
        }
        return this.toButton(op, 'btn bulk-action-bar__btn bulk-action-bar__btn--secondary');
    }

    private getSecondaryMenuParams(): IEllipsisDropdownParams {
        return {
            buttons: this.getSecondaryOperations().map((op) => this.toButton(op, 'btn btn-default')),
            icon: 'fal fa-chevron-down',
            label: 'pageshell.actions.more',
            title: 'pageshell.actions.more',
            btnClass: 'btn bulk-action-bar__btn bulk-action-bar__btn--secondary',
        };
    }

    private toButton(op: IBulkActionOperation<T>, cls: string): IDropdownButton {
        return {
            label: op.label,
            icon: op.icon,
            cls,
            enabled: () => !this.isRunning() && (op.enabled?.() ?? true),
            onClick: () => {
                void this.runOperation(op);
            },
        };
    }

    private async runOperation(op: IBulkActionOperation<T>): Promise<void> {
        if (op.enabled && !op.enabled()) {
            return;
        }
        const items = this.selected().slice();
        if (!items.length) {
            return;
        }

        if (op.intermediate) {
            // onEnter before mode so row snapshots exist before isSaveEnabled first evaluates.
            this.activeOperation(op);
            op.intermediate.onEnter?.(items);
            this.mode('intermediate');
            return;
        }

        const succeeded = await this.runWithCtx(op, (ctx) => op.onExecute?.(items, ctx));
        if (succeeded && this.shouldClearSelection(op)) {
            this.dismiss();
        }
    }

    private onCancelAllClick = (): void => {
        if (this.isRunning()) {
            // Abort only — stay in intermediate so the user can retry after cancel.
            this.abortController?.abort();
            return;
        }
        this.exitActiveWork(this.selected().slice());
    };

    private onSaveChangesClick = async (): Promise<void> => {
        const op = this.activeOperation();
        if (!op?.intermediate || this.isRunning() || !this.isSaveEnabled()) {
            return;
        }
        const items = this.selected().slice();
        const succeeded = await this.runWithCtx(op, (ctx) => op.intermediate!.onSave(items, ctx));
        if (!succeeded) {
            return;
        }
        this.resetMode();
        if (this.shouldClearSelection(op)) {
            this.dismiss();
        }
    };

    private onCloseClick = (): void => {
        if (this.isRunning()) {
            if (this.runningCancellable()) {
                this.abortController?.abort();
            }
            return;
        }
        this.dismiss();
    };

    /**
     * Runs caller work with progress + AbortSignal; returns true on success.
     * Abort / reject → false (selection kept). Default: no cancel/close while running.
     */
    private async runWithCtx(
        op: IBulkActionOperation<T>,
        work: (ctx: IBulkActionCtx) => void | Promise<unknown>,
    ): Promise<boolean> {
        const controller = new AbortController();
        this.abortController = controller;
        this.isRunning(true);
        this.progressLabel(null);
        this.runningCancellable(op.cancellable ?? false);

        const ctx: IBulkActionCtx = {
            reportProgress: (message: string) => {
                this.progressLabel(message);
            },
            signal: controller.signal,
        };

        try {
            await Promise.resolve(work(ctx));
            return !controller.signal.aborted;
        } catch {
            return false;
        } finally {
            this.isRunning(false);
            this.progressLabel(null);
            this.runningCancellable(false);
            this.abortController = null;
        }
    }

    /** Clears selection (or caller onClose) and exits intermediate; calls onCancel when leaving an active step. */
    private dismiss(): void {
        this.exitActiveWork(this.selected().slice());
        if (this.onClose) {
            this.onClose();
        } else {
            this.selected([]);
        }
    }

    /**
     * Abort in-flight work and/or cancel intermediate, then reset bar mode.
     * Idempotent — safe when dismiss clears `selected` after already unwinding.
     */
    private exitActiveWork(itemsForCancel: T[]): void {
        const op = this.activeOperation();
        const wasIntermediate = this.mode() === 'intermediate';
        this.abortController?.abort();
        // Clear mode before onCancel so a nested selected-clear does not re-enter.
        this.resetMode();
        if (wasIntermediate) {
            op?.intermediate?.onCancel?.(itemsForCancel);
        }
    }

    private resetMode(): void {
        this.mode('select');
        this.activeOperation(null);
        this.isRunning(false);
        this.progressLabel(null);
        this.runningCancellable(false);
        this.abortController = null;
    }

    private shouldClearSelection(op: IBulkActionOperation<T>): boolean {
        return op.clearSelection ?? this.instanceClearSelection;
    }

    dispose(): void {
        this.abortController?.abort();
        this.kosub.unsuscribeAll();
        this.isVisible.dispose();
        this.isIntermediate.dispose();
        this.canCancelRunning.dispose();
        this.canCancelOrClose.dispose();
        this.isSaveEnabled.dispose();
        this.hasSingleSecondary.dispose();
        this.hasSecondaryMenu.dispose();
    }
}
